import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { ExamsApi } from './api/exams'
import { slugOf } from './chosenExam'

/**
 * The front door when no Exam has been chosen yet: one card per Exam, each an address.
 *
 * Once an Exam has been chosen the site opens on it (app:R84), so this is seen on the first
 * visit and after the switcher's Exam has gone from the catalogue; the switcher in the
 * masthead is the everyday way to change Exam.
 */
export function ChooseExam({ exams }: { exams: ExamsApi }) {
  const catalogue = useQuery({ queryKey: ['exams'], queryFn: () => exams.list() })

  return (
    <>
      <h2 className="font-heading text-h2 font-bold text-heading">Choose an Exam</h2>
      <p className="mt-2 text-body">
        Everything in the Admin Site is about one Exam at a time. Pick the one to work on; the
        address carries it from then on.
      </p>

      {catalogue.isError && (
        <p role="alert" className="mt-4 border-l-2 border-red-700 pl-3 text-red-700">
          {catalogue.error.message}
        </p>
      )}

      {catalogue.isPending && <p className="mt-4 text-muted">Reading the Exams...</p>}

      {catalogue.data?.length === 0 && (
        <p className="mt-4 text-muted">No Exam in the catalogue yet.</p>
      )}

      <ul className="mt-6 grid gap-4 sm:grid-cols-2">
        {(catalogue.data ?? []).map((exam) => (
          <li key={exam.code}>
            <Link
              to="/$exam"
              params={{ exam: slugOf(exam.code) }}
              className="block rounded-lg border border-ink-100 bg-card p-6 hover:border-green-800 hover:bg-green-050"
            >
              <span className="font-heading text-h3 font-bold text-heading">{exam.code}</span>
              <span className="mt-1 block text-body">{exam.name}</span>
              <span className="mt-3 block text-muted">
                {exam.sittings.length === 0
                  ? 'No Sitting entered'
                  : `${exam.sittings.length} ${exam.sittings.length === 1 ? 'Sitting' : 'Sittings'} entered`}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </>
  )
}
